import { urlPaths } from "enums/urlPaths";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const usePaceDashboard = (): {
    overallRef: React.RefObject<HTMLDivElement>;
    boxplotRef: React.RefObject<HTMLDivElement>;
    heatmapRef: React.RefObject<HTMLDivElement>;
    hubAnalysisRef: React.RefObject<HTMLDivElement>;
    engagementSummaryRef: React.RefObject<HTMLDivElement>;
    activeSection: string;
    behaviouralSelectedValue: string;
    anchorEl: HTMLElement | null;
    openLockMessage: boolean;
    lockMessageAnchorEl: HTMLButtonElement | null;
    handlePreFilteredRedirect: (status?: string) => void;
    handleSelectClick: (ev: React.SyntheticEvent) => void;
    handleCloseSelect: () => void;
    handleBehaviouralChange: (value: string) => void;
    handleScrollTo: (ref: React.RefObject<HTMLDivElement | null>) => void;
    handleOpenLockedMessage: (event?: React.MouseEvent<HTMLButtonElement>) => void;
    onCloseLockMessage: () => void;
} => {
    const navigate = useNavigate();

    const overallRef = useRef<HTMLDivElement | null>(null);
    const boxplotRef = useRef<HTMLDivElement | null>(null);
    const heatmapRef = useRef<HTMLDivElement | null>(null);
    const hubAnalysisRef = useRef<HTMLDivElement | null>(null);
    const engagementSummaryRef = useRef<HTMLDivElement | null>(null);

    const [activeSection, setActiveSection] = useState<string>("");

    const handlePreFilteredRedirect = (status?: string) => {
        if (status) {
            navigate(`${urlPaths.PacePortal}?filter_pace_status=${status}`);
        } else {
            navigate(urlPaths.PacePortal);
        }
    };

    const [behaviouralSelectedValue, setBehaviouralSelectedValue] = useState<string>("");
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const [lockMessageAnchorEl, setLockMessageAnchorEl] = useState<HTMLButtonElement | null>(null);
    const openLockMessage = Boolean(lockMessageAnchorEl);

    const handleOpenLockedMessage = (event?: React.MouseEvent<HTMLButtonElement>) => {
        if (event) {
            setLockMessageAnchorEl(event.currentTarget);
        }
    };

    const onCloseLockMessage = () => {
        setLockMessageAnchorEl(null);
    };

    const handleSelectClick = (ev: React.SyntheticEvent) => {
        setAnchorEl(ev.currentTarget as HTMLElement);
    };

    const handleCloseSelect = () => {
        setAnchorEl(null);
    };

    const handleBehaviouralChange = (value: string) => {
        setBehaviouralSelectedValue(value);
        handleCloseSelect();
    };

    const handleScrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
        if (!ref.current) return;

        const headerHeight = 104;
        const hubAnalysisHeight = hubAnalysisRef.current ? hubAnalysisRef.current.getBoundingClientRect().height : 0;
        const offset = headerHeight + hubAnalysisHeight;
        const elementPosition = ref.current.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - offset, behavior: "smooth" });
    };

    useEffect(() => {
        const headerHeight = 240; // Main Header + Hub Analysis sticky Header + active section's top padding
        const handleScroll = () => {
            const scrollPos = window.scrollY + headerHeight;

            // offsetTop (vertical position) of each section
            const overallOffset = overallRef.current?.offsetTop || 0;
            const boxplotOffset = boxplotRef.current?.offsetTop || 0;
            const heatmapOffset = heatmapRef.current?.offsetTop || 0;

            let active = "";
            if (scrollPos >= heatmapOffset) {
                active = "heatmap";
            } else if (scrollPos >= boxplotOffset) {
                active = "boxplot";
            } else if (scrollPos >= overallOffset) {
                active = "overall";
            } else {
                active = "";
            }
            setActiveSection(active);
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return {
        overallRef,
        boxplotRef,
        heatmapRef,
        hubAnalysisRef,
        engagementSummaryRef,
        activeSection,
        behaviouralSelectedValue,
        anchorEl,
        openLockMessage,
        lockMessageAnchorEl,
        handlePreFilteredRedirect,
        handleSelectClick,
        handleCloseSelect,
        handleBehaviouralChange,
        handleScrollTo,
        handleOpenLockedMessage,
        onCloseLockMessage,
    };
};

export default usePaceDashboard;
